import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { formatPrice } from '@/components/PropertyCard';

export default function MapView({ properties = [], center, zoom = 4, className = '', selectedId = null, onBoundsChange = null, fitToProperties = true }) {
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const markersRef = useRef([]);
  const debounceRef = useRef(null);
  const navigate = useNavigate();

  // Default center: Venezuela
  const defaultCenter = center || [8.0, -66.0];

  useEffect(() => {
    if (!mapRef.current) return;

    if (!mapInstanceRef.current) {
      mapInstanceRef.current = L.map(mapRef.current, {
        center: defaultCenter,
        zoom: zoom,
        scrollWheelZoom: true,
        zoomControl: true,
      });

      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
        maxZoom: 19,
      }).addTo(mapInstanceRef.current);

      // Emit visible-area changes (debounced) when the caller is interested
      // — used by "Search by Map" mode to live-filter the result list.
      const emit = () => {
        if (!onBoundsChange || !mapInstanceRef.current) return;
        const b = mapInstanceRef.current.getBounds();
        onBoundsChange({
          west: b.getWest(),
          south: b.getSouth(),
          east: b.getEast(),
          north: b.getNorth(),
          bbox: `${b.getWest()},${b.getSouth()},${b.getEast()},${b.getNorth()}`,
        });
      };
      const debounced = () => {
        if (debounceRef.current) clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(emit, 350);
      };
      mapInstanceRef.current.on('moveend', debounced);
      mapInstanceRef.current.on('zoomend', debounced);
    }

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      if (mapInstanceRef.current) {
        mapInstanceRef.current.remove();
        mapInstanceRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!mapInstanceRef.current) return;

    // Clear existing markers
    markersRef.current.forEach(m => m.remove());
    markersRef.current = [];

    const validProperties = properties.filter(p => p.latitude && p.longitude && p.latitude !== 0 && p.longitude !== 0);

    validProperties.forEach(property => {
      const isSelected = property.id === selectedId;
      const icon = L.divIcon({
        className: 'custom-marker',
        html: `<div class="property-marker ${isSelected ? 'active' : ''}">${formatPrice(property.price, property.currency, property.operation_type)}</div>`,
        iconSize: [0, 0],
        iconAnchor: [40, 15],
      });

      const marker = L.marker([property.latitude, property.longitude], { icon })
        .addTo(mapInstanceRef.current)
        .on('click', () => navigate(`/property/${property.id}`));

      marker.bindTooltip(property.title, { direction: 'top', offset: [0, -10] });
      markersRef.current.push(marker);
    });

    // Recenter the map on every render when we have valid markers.
    // For a single property: honor the caller's `center`/`zoom` props
    // (or fall back to that property's coords) — fitBounds on one point
    // produces a degenerate bbox and Leaflet drifts to a regional view.
    // In "Search by Map" mode (onBoundsChange callback set + fitToProperties=false)
    // we DON'T auto-fit, otherwise the map would jump every time the user pans.
    if (!fitToProperties) {
      // parent controls the viewport; nothing to do
    } else if (validProperties.length === 1) {
      const p = validProperties[0];
      const focus = center || [p.latitude, p.longitude];
      mapInstanceRef.current.setView(focus, zoom, { animate: false });
    } else if (validProperties.length > 1) {
      const bounds = L.latLngBounds(validProperties.map(p => [p.latitude, p.longitude]));
      mapInstanceRef.current.fitBounds(bounds, { padding: [50, 50], maxZoom: 14 });
    }
  }, [properties, selectedId, navigate, center, zoom, fitToProperties]);

  return (
    <div ref={mapRef} className={`rounded-xl overflow-hidden ${className}`} data-testid="map-view"
      style={{ height: '100%', minHeight: '300px' }} />
  );
}
